import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
    DollarIcon,
    PlusIcon,
    SearchIcon,
    DeleteIcon,
    EditIcon,
    SaveIcon,
    CloseIcon,
    UploadIcon,
    ExternalLinkIcon,
    TrendingUpIcon,
    CalendarIcon,
    CheckCircleIcon,
    AlertCircleIcon,
    RefreshIcon,
    DownloadIcon
} from '../components/Icons';

const CATEGORIES = [
    'Mitgliedsbeiträge',
    'Spenden & Förderungen',
    'Lager & Fahrten',
    'Material & Ausrüstung',
    'Verpflegung & Küche',
    'Heim & Instandhaltung',
    'Aktionen & Events',
    'Sonstiges'
];

const ACCOUNTS = [
    'Barkasse',
    'Girokonto',
    'Sparkonto',
    'Projektkasse'
];

const FinanceManagement = () => {
    const { user, API_URL } = useContext(AuthContext);
    const token = user?.token;

    const [summary, setSummary] = useState({
        totalBalance: 0,
        totalIncome: 0,
        totalExpense: 0,
        accountBalances: {},
        categoryTotals: {},
        recentTransactions: [],
        count: 0
    });

    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [accountFilter, setAccountFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        type: 'expense',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        category: 'Material & Ausrüstung',
        account: 'Barkasse',
        receiptUrl: '',
        notes: '',
        linkedEvent: ''
    });
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const authConfig = useMemo(() => ({
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    const fetchData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [summaryRes, txRes] = await Promise.all([
                axios.get(`${API_URL}/finances/summary`, authConfig),
                axios.get(`${API_URL}/finances/transactions`, authConfig)
            ]);
            setSummary(summaryRes.data);
            setTransactions(txRes.data);
        } catch (err) {
            console.error('Fehler beim Laden der Finanzdaten:', err);
            setError(err.response?.data?.message || 'Fehler beim Laden der Finanzdaten.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchData();
        }
    }, [token, API_URL]);

    const handleOpenCreateModal = () => {
        setEditingTransaction(null);
        setFormData({
            title: '',
            type: 'expense',
            amount: '',
            date: new Date().toISOString().split('T')[0],
            category: 'Material & Ausrüstung',
            account: 'Barkasse',
            receiptUrl: '',
            notes: '',
            linkedEvent: ''
        });
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (tx) => {
        setEditingTransaction(tx);
        setFormData({
            title: tx.title || '',
            type: tx.type || 'expense',
            amount: tx.amount || '',
            date: tx.date || new Date().toISOString().split('T')[0],
            category: tx.category || 'Sonstiges',
            account: tx.account || 'Barkasse',
            receiptUrl: tx.receiptUrl || '',
            notes: tx.notes || '',
            linkedEvent: tx.linkedEvent || ''
        });
        setIsModalOpen(true);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        const uploadForm = new FormData();
        uploadForm.append('file', file);

        try {
            const res = await axios.post(`${API_URL}/files/upload`, uploadForm, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            const uploadedUrl = res.data?.url || res.data?.fileUrl;
            if (uploadedUrl) {
                setFormData(prev => ({ ...prev, receiptUrl: uploadedUrl }));
            }
        } catch (err) {
            console.error('Beleg-Upload fehlgeschlagen:', err);
            alert('Fehler beim Hochladen des Belegs: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || formData.amount === '') {
            alert('Bitte Titel und Betrag ausfüllen.');
            return;
        }

        setIsSubmitting(true);
        try {
            if (editingTransaction) {
                await axios.put(`${API_URL}/finances/transactions/${editingTransaction._id}`, formData, authConfig);
                setSuccessMessage('Buchung erfolgreich aktualisiert!');
            } else {
                await axios.post(`${API_URL}/finances/transactions`, formData, authConfig);
                setSuccessMessage('Buchung erfolgreich erfasst!');
            }
            setIsModalOpen(false);
            fetchData();
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            console.error('Fehler beim Speichern der Buchung:', err);
            alert('Fehler beim Speichern: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Möchtest du diese Buchung wirklich unwiderruflich löschen?')) return;
        try {
            await axios.delete(`${API_URL}/finances/transactions/${id}`, authConfig);
            setSuccessMessage('Buchung gelöscht.');
            fetchData();
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            console.error('Fehler beim Löschen:', err);
            alert('Fehler beim Löschen: ' + (err.response?.data?.message || err.message));
        }
    };

    // Filter transactions
    const filteredTransactions = useMemo(() => {
        return transactions.filter(t => {
            if (typeFilter !== 'all' && t.type !== typeFilter) return false;
            if (accountFilter !== 'all' && t.account !== accountFilter) return false;
            if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matchTitle = t.title?.toLowerCase().includes(term);
                const matchNotes = t.notes?.toLowerCase().includes(term);
                const matchCat = t.category?.toLowerCase().includes(term);
                if (!matchTitle && !matchNotes && !matchCat) return false;
            }
            return true;
        });
    }, [transactions, typeFilter, accountFilter, categoryFilter, searchTerm]);

    // CSV Export
    const handleExportCSV = () => {
        const headers = ['Datum', 'Titel', 'Typ', 'Betrag (EUR)', 'Konto', 'Kategorie', 'Notizen', 'Beleg-URL'];
        const rows = filteredTransactions.map(t => [
            t.date || '',
            `"${(t.title || '').replace(/"/g, '""')}"`,
            t.type === 'income' ? 'Einnahme' : 'Ausgabe',
            Number(t.amount || 0).toFixed(2),
            t.account || 'Barkasse',
            t.category || 'Sonstiges',
            `"${(t.notes || '').replace(/"/g, '""')}"`,
            t.receiptUrl || ''
        ]);

        const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(';'), ...rows.map(e => e.join(';'))].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `Kassenbuch_Export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shadow-sm">
                            <DollarIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-extrabold text-surface-on tracking-tight">
                                Kassenbuch & Finanzen
                            </h1>
                            <p className="text-sm text-surface-onVariant mt-0.5">
                                Finanzübersicht, Einnahmen, Ausgaben & Belegverwaltung für den Kassenwart
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={fetchData}
                        className="p-2.5 rounded-2xl border border-outlineVariant/50 bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-onVariant transition-all shadow-sm"
                        title="Aktualisieren"
                    >
                        <RefreshIcon className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleExportCSV}
                        className="px-4 py-2.5 rounded-2xl border border-outlineVariant/50 bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-on text-sm font-semibold flex items-center gap-2 transition-all shadow-sm"
                    >
                        <DownloadIcon className="w-4 h-4" />
                        CSV Export
                    </button>
                    <button
                        onClick={handleOpenCreateModal}
                        className="px-5 py-2.5 rounded-2xl bg-primary text-primary-on hover:bg-primary/90 text-sm font-bold flex items-center gap-2 transition-all shadow-md active:scale-95"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Buchung erfassen
                    </button>
                </div>
            </div>

            {/* Notification messages */}
            {successMessage && (
                <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 flex items-center gap-3 animate-fade-in shadow-sm">
                    <CheckCircleIcon className="w-5 h-5 shrink-0" />
                    <p className="text-sm font-medium">{successMessage}</p>
                </div>
            )}

            {error && (
                <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 flex items-center gap-3 animate-fade-in shadow-sm">
                    <AlertCircleIcon className="w-5 h-5 shrink-0" />
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}

            {/* KPI Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Balance */}
                <div className="p-6 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm relative overflow-hidden">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-surface-onVariant">Gesamtsaldo</span>
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold ${summary.totalBalance >= 0 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'}`}>
                            {summary.totalBalance >= 0 ? '✓' : '!'}
                        </div>
                    </div>
                    <div className="mt-3">
                        <h3 className={`text-3xl font-black tracking-tight ${summary.totalBalance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                            {summary.totalBalance.toFixed(2)} €
                        </h3>
                        <p className="text-xs text-surface-onVariant mt-1">Über alle aktiven Konten</p>
                    </div>
                </div>

                {/* Total Income */}
                <div className="p-6 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-surface-onVariant">Einnahmen (gesamt)</span>
                        <span className="text-emerald-500 text-lg">↗</span>
                    </div>
                    <div className="mt-3">
                        <h3 className="text-3xl font-black text-surface-on tracking-tight text-emerald-600 dark:text-emerald-400">
                            +{summary.totalIncome.toFixed(2)} €
                        </h3>
                        <p className="text-xs text-surface-onVariant mt-1">Beiträge, Spenden & Zuschüsse</p>
                    </div>
                </div>

                {/* Total Expense */}
                <div className="p-6 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-surface-onVariant">Ausgaben (gesamt)</span>
                        <span className="text-rose-500 text-lg">↘</span>
                    </div>
                    <div className="mt-3">
                        <h3 className="text-3xl font-black text-surface-on tracking-tight text-rose-600 dark:text-rose-400">
                            -{summary.totalExpense.toFixed(2)} €
                        </h3>
                        <p className="text-xs text-surface-onVariant mt-1">Material, Verpflegung & Aktionen</p>
                    </div>
                </div>

                {/* Account Balances Breakdown */}
                <div className="p-6 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm flex flex-col justify-between">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-surface-onVariant">Kontenstände</span>
                        <div className="mt-2 space-y-1.5 max-h-24 overflow-y-auto pr-1">
                            {Object.keys(summary.accountBalances || {}).length === 0 ? (
                                <p className="text-xs text-surface-onVariant">Noch keine Buchungen</p>
                            ) : (
                                Object.entries(summary.accountBalances).map(([acc, bal]) => (
                                    <div key={acc} className="flex items-center justify-between text-xs">
                                        <span className="text-surface-on font-medium truncate max-w-[110px]">{acc}:</span>
                                        <span className={`font-bold ${bal >= 0 ? 'text-surface-on' : 'text-rose-500'}`}>
                                            {bal.toFixed(2)} €
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="p-5 rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
                    {/* Search */}
                    <div className="relative flex-1">
                        <SearchIcon className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-onVariant pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Buchungen nach Titel, Kategorie oder Notiz durchsuchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary transition-all"
                        />
                    </div>

                    {/* Type Filter */}
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="px-3.5 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary font-medium"
                    >
                        <option value="all">Alle Typen</option>
                        <option value="income">Nur Einnahmen (+)</option>
                        <option value="expense">Nur Ausgaben (-)</option>
                    </select>

                    {/* Account Filter */}
                    <select
                        value={accountFilter}
                        onChange={(e) => setAccountFilter(e.target.value)}
                        className="px-3.5 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary font-medium"
                    >
                        <option value="all">Alle Konten</option>
                        {ACCOUNTS.map(a => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </select>

                    {/* Category Filter */}
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="px-3.5 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary font-medium"
                    >
                        <option value="all">Alle Kategorien</option>
                        {CATEGORIES.map(c => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Transactions Table */}
            <div className="rounded-3xl bg-surface-container border border-outlineVariant/30 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-outlineVariant/20 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-surface-on">Kassenbuch-Einträge</h2>
                        <p className="text-xs text-surface-onVariant">
                            {filteredTransactions.length} Buchung{filteredTransactions.length === 1 ? '' : 'en'} gefunden
                        </p>
                    </div>
                </div>

                {isLoading ? (
                    <div className="p-12 text-center text-surface-onVariant">
                        <RefreshIcon className="w-8 h-8 animate-spin mx-auto text-primary mb-2" />
                        <p className="text-sm font-medium">Finanzbuchungen werden geladen...</p>
                    </div>
                ) : filteredTransactions.length === 0 ? (
                    <div className="p-12 text-center text-surface-onVariant space-y-3">
                        <div className="w-12 h-12 rounded-2xl bg-surface-containerHighest flex items-center justify-center mx-auto text-primary">
                            <DollarIcon className="w-6 h-6" />
                        </div>
                        <p className="text-base font-semibold text-surface-on">Keine Buchungen vorhanden</p>
                        <p className="text-sm max-w-md mx-auto">
                            Es wurden keine passenden Einträge gefunden. Klicke auf "Buchung erfassen", um deine erste Einnahme oder Ausgabe im Kassenbuch zu speichern.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-outlineVariant/20 bg-surface-containerHigh/50 text-surface-onVariant text-xs uppercase font-bold tracking-wider">
                                    <th className="py-3.5 px-4">Datum</th>
                                    <th className="py-3.5 px-4">Vorgang / Titel</th>
                                    <th className="py-3.5 px-4">Kategorie</th>
                                    <th className="py-3.5 px-4">Konto</th>
                                    <th className="py-3.5 px-4 text-right">Betrag</th>
                                    <th className="py-3.5 px-4 text-center">Beleg</th>
                                    <th className="py-3.5 px-4 text-right">Aktionen</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-outlineVariant/10">
                                {filteredTransactions.map((tx) => {
                                    const isIncome = tx.type === 'income';
                                    return (
                                        <tr key={tx._id} className="hover:bg-surface-containerHigh/30 transition-colors">
                                            <td className="py-3.5 px-4 font-mono text-xs text-surface-onVariant whitespace-nowrap">
                                                {tx.date || '-'}
                                            </td>
                                            <td className="py-3.5 px-4 font-semibold text-surface-on">
                                                <div>{tx.title}</div>
                                                {tx.notes && (
                                                    <div className="text-xs font-normal text-surface-onVariant mt-0.5 line-clamp-1">
                                                        {tx.notes}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-3.5 px-4">
                                                <span className="px-2.5 py-1 rounded-full bg-surface-containerHighest text-surface-on text-xs font-medium whitespace-nowrap">
                                                    {tx.category || 'Sonstiges'}
                                                </span>
                                            </td>
                                            <td className="py-3.5 px-4">
                                                <span className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold whitespace-nowrap">
                                                    {tx.account || 'Barkasse'}
                                                </span>
                                            </td>
                                            <td className={`py-3.5 px-4 text-right font-black whitespace-nowrap ${isIncome ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                {isIncome ? '+' : '-'}{Number(tx.amount || 0).toFixed(2)} €
                                            </td>
                                            <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                                {tx.receiptUrl ? (
                                                    <a
                                                        href={tx.receiptUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-semibold"
                                                        title="Beleg öffnen"
                                                    >
                                                        <ExternalLinkIcon className="w-3.5 h-3.5" /> Beleg
                                                    </a>
                                                ) : (
                                                    <span className="text-xs text-surface-onVariant opacity-40">-</span>
                                                )}
                                            </td>
                                            <td className="py-3.5 px-4 text-right whitespace-nowrap space-x-1">
                                                <button
                                                    onClick={() => handleOpenEditModal(tx)}
                                                    className="p-1.5 rounded-xl hover:bg-surface-containerHighest text-surface-onVariant hover:text-surface-on transition-colors"
                                                    title="Bearbeiten"
                                                >
                                                    <EditIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(tx._id)}
                                                    className="p-1.5 rounded-xl hover:bg-rose-500/10 text-surface-onVariant hover:text-rose-600 transition-colors"
                                                    title="Löschen"
                                                >
                                                    <DeleteIcon className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create / Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
                    <div className="w-full max-w-lg rounded-3xl bg-surface-containerHigh border border-outlineVariant/40 shadow-2xl p-6 md:p-8 space-y-6 my-8">
                        <div className="flex items-center justify-between border-b border-outlineVariant/20 pb-4">
                            <h3 className="text-xl font-bold text-surface-on">
                                {editingTransaction ? 'Buchung bearbeiten' : 'Neue Buchung erfassen'}
                            </h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2 rounded-full hover:bg-surface-containerHighest text-surface-onVariant transition-colors"
                            >
                                <CloseIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Typ: Einnahme vs Ausgabe */}
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-2">
                                    Buchungsart
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, type: 'expense' })}
                                        className={`py-3 rounded-2xl font-bold text-sm border flex items-center justify-center gap-2 transition-all ${formData.type === 'expense' ? 'bg-rose-500 text-white border-rose-600 shadow-md' : 'bg-surface-containerLowest border-outlineVariant/40 text-surface-on opacity-70'}`}
                                    >
                                        ↘ Ausgabe (-)
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, type: 'income' })}
                                        className={`py-3 rounded-2xl font-bold text-sm border flex items-center justify-center gap-2 transition-all ${formData.type === 'income' ? 'bg-emerald-600 text-white border-emerald-700 shadow-md' : 'bg-surface-containerLowest border-outlineVariant/40 text-surface-on opacity-70'}`}
                                    >
                                        ↗ Einnahme (+)
                                    </button>
                                </div>
                            </div>

                            {/* Titel */}
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                    Titel / Verwendungszweck *
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="z.B. Zeltstangen Reparatur, Pfadfinderbeitrag Q3..."
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary"
                                />
                            </div>

                            {/* Betrag & Datum */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Betrag (€) *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        required
                                        min="0"
                                        placeholder="0.00"
                                        value={formData.amount}
                                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm font-bold focus:outline-none focus:border-primary"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Datum *
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary"
                                    />
                                </div>
                            </div>

                            {/* Konto & Kategorie */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Konto
                                    </label>
                                    <select
                                        value={formData.account}
                                        onChange={(e) => setFormData({ ...formData, account: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary font-medium"
                                    >
                                        {ACCOUNTS.map(a => (
                                            <option key={a} value={a}>{a}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                        Kategorie
                                    </label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary font-medium"
                                    >
                                        {CATEGORIES.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Beleg Upload */}
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                    Beleg / Quittung (Foto oder PDF)
                                </label>
                                <div className="flex items-center gap-3">
                                    <label className="cursor-pointer px-4 py-2.5 rounded-2xl border border-outlineVariant/40 bg-surface-containerLowest hover:bg-surface-container text-surface-on text-xs font-semibold flex items-center gap-2 transition-all">
                                        <UploadIcon className="w-4 h-4" />
                                        {isUploading ? 'Lädt hoch...' : 'Datei hochladen'}
                                        <input
                                            type="file"
                                            accept="image/*,application/pdf"
                                            onChange={handleFileUpload}
                                            disabled={isUploading}
                                            className="hidden"
                                        />
                                    </label>
                                    {formData.receiptUrl && (
                                        <a
                                            href={formData.receiptUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-primary hover:underline font-semibold flex items-center gap-1 truncate max-w-[200px]"
                                        >
                                            📎 Vorhandener Beleg
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Notizen */}
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-surface-onVariant mb-1.5">
                                    Notizen / Bemerkungen
                                </label>
                                <textarea
                                    rows="2"
                                    placeholder="Optionale Details zur Buchung..."
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on text-sm focus:outline-none focus:border-primary resize-none"
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-outlineVariant/20">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-5 py-2.5 rounded-2xl text-surface-onVariant hover:text-surface-on text-sm font-semibold transition-colors"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || isUploading}
                                    className="px-6 py-2.5 rounded-2xl bg-primary text-primary-on hover:bg-primary/90 text-sm font-bold flex items-center gap-2 transition-all shadow-md disabled:opacity-50"
                                >
                                    <SaveIcon className="w-4 h-4" />
                                    {isSubmitting ? 'Speichert...' : editingTransaction ? 'Änderungen speichern' : 'Buchung erfassen'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanceManagement;
